import React from 'react';
import {   InfoBox,   ExerciseBox,   CodeBlock,   InlineCode,  Result, SectionTitle, Separator,CorrectionBox, Image,  Note,  TableContainer,  Highlight,  PatternContainer,  SectionHeading,  SubHeading,  TwoColGrid,  ThreeColGrid,  GridItem,  Divider} from '../../../../components/course/CourseComponents';
import screen5 from '../screen5.jpg';

const Chapitre3Content = () => {
  return (
    <>
    <InfoBox title="Objectif du chapitre" color="indigo">
      <p className="mb-0">
        Créer un tableau pour mémoriser l'état du jeu et gérer l'alternance des joueurs.
      </p>
    </InfoBox>      

    <Separator type="text" text="Création d'un tableau pour mémoriser l'état du jeu" color="gray-darker" size="md" />
    <SectionTitle>🏁 1/ Création d'un tableau pour mémoriser l'état du jeu</SectionTitle>

    <p>Pour mémoriser l'état du plateau, on va créer un tableau à 2 dimensions de taille 3x3.</p>
    <p>Ce tableau sera vide au départ, et on le remplira au fur et à mesure que les joueurs cliquent sur les cases.</p>
    <p>Chaque case du plateau correspond à un emplacement dans le tableau : </p>
    <Image src={screen5} alt="Grille du Morpion" width="370px" height="370px" borderRadius="xl" shadow={true} border={false} className="object-contain" />

    <p>Ce tableau permet de suivre l'état du jeu. Il est très important de le faire pour :</p>
    <ul className="list-disc list-inside mb-4">
      <li>empêcher un joueur de rejouer dans une case déjà occupée</li>
      <li>pouvoir détecter le gagnant</li>
    </ul>
    

    <p>Pour cela, on va créer une variable en global :</p>
    <CodeBlock language="javascript">
{`let grille;`}
    </CodeBlock>

    <p>Et l'initialiser dans <InlineCode>setup()</InlineCode> :</p>
    <CodeBlock language="javascript">
{`grille = [
  ["", "", ""],
  ["", "", ""],
  ["", "", ""]
];`}
    </CodeBlock>

    <p>Quand un joueur clique sur une case, on modifie la valeur correspondante dans le tableau pour y mettre une croix "X" ou un cercle "O".</p>
    <p>On connaît les coordonnées de la case cliquée dans <InlineCode>mousePressed()</InlineCode> grâce aux variables <InlineCode>case_x</InlineCode> et <InlineCode>case_y</InlineCode> (voir chapitre précédent).</p>

    <CodeBlock language="javascript">
{`grille[case_x][case_y] = "X";`}
    </CodeBlock>

    <p><strong>Exemple :</strong></p>
    <p>Si le joueur clique sur la case ligne 0, colonne 0 (en haut à gauche), on met "X" dans <InlineCode>grille[0][0]</InlineCode>.</p>

    <Note type="warning" title="⚠️ Important">
      Avant de mettre un symbole, il faut vérifier que la case est vide. Si elle contient déjà une valeur, on ne fait rien.
    </Note>

    <Note type="exercice" title="🧪 À toi de jouer : création de la grille">
      Crée de la grille :<br/>
      &nbsp;&nbsp;- Crée la variable <InlineCode>grille</InlineCode> en global<br/>
      &nbsp;&nbsp;- Initialise le tableau avec des cases vides dans <InlineCode>setup()</InlineCode><br/>
      Dans <InlineCode>mousePressed()</InlineCode> :<br/>
      &nbsp;&nbsp;- Vérifie que la case est vide<br/>
      &nbsp;&nbsp;- Mets un "X" dans la bonne case<br/>
      Pour vérifier si ça marche :<br/>
      &nbsp;&nbsp;- Affiche le tableau dans <InlineCode>draw()</InlineCode> en faisant <InlineCode>print(grille);</InlineCode><br/>
      <br/>
      <Result>Quand tu cliques sur une case, un "X" apparaît dans le tableau au bon endroit.</Result>
      <CorrectionBox>
        <CodeBlock language="javascript">
{`let grille;

function setup() {
  // ...
  grille = [
    ["", "", ""],
    ["", "", ""],
    ["", "", ""]
  ];
}

function draw() {
  // ...
  print(grille);
}

function mousePressed() {
  let case_x = floor(mouseY / hauteur_case);
  let case_y = floor(mouseX / largeur_case);
  
  if (case_x < 0 || case_x > 2 || case_y < 0 || case_y > 2)  
    return; // Clique en dehors du plateau
  
  if (grille[case_x][case_y] == "") {
    grille[case_x][case_y] = "X";
  }
}`}
        </CodeBlock>
      </CorrectionBox>
    </Note>

    <Separator type="text" text="Changement de joueur" color="gray-darker" size="md" />
    <SectionTitle>🔄 2/ Changement de joueur</SectionTitle>

    <p>On veut que chaque clic change le joueur.</p>
    <p><strong>Exemple :</strong></p>
    <ul className="list-disc list-inside mb-4">
      <li>1er clic → "X"</li>
      <li>2e clic → "O"</li>
      <li>3e clic → "X"</li>
      <li>etc.</li>
    </ul>

    <p>Pour cela, on va créer une variable en global :</p>
    <CodeBlock language="javascript">
{`let joueur_courant = "X";`}
    </CodeBlock>

    <ul className="list-disc list-inside mb-4">
      <li>Si <InlineCode>joueur_courant == "X"</InlineCode> → c'est au joueur 1 de jouer</li>
      <li>Si <InlineCode>joueur_courant == "O"</InlineCode> → c'est au joueur 2</li>
    </ul>

    <p>Quand un joueur joue dans une case vide, on change la valeur de <InlineCode>joueur_courant</InlineCode> :</p>
    <CodeBlock language="javascript">
{`if (joueur_courant == "X") joueur_courant = "O";
else joueur_courant = "X";`}
    </CodeBlock>

    <Note type="exercice" title="🧪 À toi de jouer : changement de joueur">
      Dans <InlineCode>mousePressed()</InlineCode>, si la case est vide :<br/>
      &nbsp;&nbsp;- ajoute le symbole du joueur courant<br/>
      &nbsp;&nbsp;- change de joueur<br/>
      <br/>
      <Result>Le tableau se remplit avec des "X" et "O" en alternance, en fonction du tour de jeu.</Result>

      <CorrectionBox>
        <CodeBlock language="javascript">
{`let joueur_courant = "X";

function mousePressed() {
  // ...
  if (grille[case_x][case_y] == "") { 
    grille[case_x][case_y] = joueur_courant;
    
    // Changement de joueur
    if (joueur_courant == "X") joueur_courant = "O";
    else joueur_courant = "X";
  }
}`}
        </CodeBlock>
      </CorrectionBox>
    </Note>

    <Separator type="text" text="Code complet" color="gray-darker" size="md" />
    <SectionTitle>✅ 3/ Code complet (Gestion des joueurs et de la grille)</SectionTitle>

          <CodeBlock language="javascript">
{`// Jeu 2 : le Morpion

let largeur_case;
let hauteur_case;
let grille;
let joueur_courant = "X";

function setup() {
  createCanvas(600, 600);
  largeur_case = width / 3;
  hauteur_case = height / 3;
  
  grille = [
    ["", "", ""],
    ["", "", ""],
    ["", "", ""]
  ];
}

function draw() {
  background(220, 220, 220);

  // Plateau de jeu
  strokeWeight(4);
  for (let i = 1; i < 3; i++) {
    line(i * largeur_case, 0, i * largeur_case, height);
    line(0, i * hauteur_case, width, i * hauteur_case);
  }
  
  // Affichage pour débugger
  print(grille);
}

function mousePressed() {
  let case_x = floor(mouseY / hauteur_case);
  let case_y = floor(mouseX / largeur_case);

  if (case_x < 0 || case_x > 2 || case_y < 0 || case_y > 2)  
    return; // Clique en dehors du plateau
  
  if (grille[case_x][case_y] == "") {
    grille[case_x][case_y] = joueur_courant;
    
    // Changement de joueur
    if (joueur_courant == "X") joueur_courant = "O";
    else joueur_courant = "X";
  }
}`}
    </CodeBlock>

    <Note type="info" title="💡 Prêt pour la suite ?">
      Maintenant que tu as la logique de base du morpion, nous allons afficher les X et O sur le plateau !
    </Note>

   
  </>
  );
};

export default Chapitre3Content; 